import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { AreaSelectionMarknadsomrade } from "~app/common/models/areaSelector";
import { LanDTO } from "~app/common/models/distrikt/LanDTO";
import { BefraktareDto } from "~app/common/models/styrtabeller/BefraktareDto";
import { MottagningsplatsDTO } from "~app/common/models/styrtabeller/MottagningsplatsDTO";
import { RedovisningshanvisningDto } from "~app/common/models/styrtabeller/RedovisningshanvisningDto";
import { StyrtabellLanKommunDistriktDTO } from "~app/common/models/styrtabeller/StyrtabellLanKommunDistriktDTO";
import { StyrtabellMatningsinformationDto } from "~app/common/models/styrtabeller/StyrtabellMatningsinformationDto";
import { StyrtabellRedovisningshanvisningDto } from "~app/common/models/styrtabeller/StyrtabellRedovisningshanvisningDto";
import { ApiClientService } from "~app/common/services/api-client.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IRegelverkViol3SearchModel, IVerksamhetsomraden } from "./regelverkViol3.models";

@Injectable({
    providedIn: "root"
})
export class RegelverkViol3Service {
    private readonly apiClientService = inject(ApiClientService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    getMarknadsOmraden(): Observable<AreaSelectionMarknadsomrade[]> {
        return this.apiClientService.get("verksamhetsomrade/marknadsomrade");
    }

    getLkd(): Observable<LanDTO[]> {
        return this.apiClientService.get("distrikt/LanKommunerDistrikt");
    }

    getMottagningsplatser(): Observable<MottagningsplatsDTO[]> {
        return this.apiClientService.get("administration/styrtabeller/mottagningsplatser");
    }

    getRedovisningshanvisningar(): Observable<RedovisningshanvisningDto[]> {
        return this.apiClientService.get("administration/styrtabeller/redovisningshanvisningar");
    }

    getBefraktare(): Observable<BefraktareDto[]> {
        return this.apiClientService.get("administration/styrtabeller/befraktare");
    }

    getSearchFilter(key: string): IRegelverkViol3SearchModel {
        return this.webinstallningarService.get(key);
    }

    saveSearchFilter(searchModel: IRegelverkViol3SearchModel, key: string) {
        this.webinstallningarService.set(key, searchModel);
    }

    searchRedovisningshanvisning(searchModel: IRegelverkViol3SearchModel, selectVerksamhetsomraden: IVerksamhetsomraden[]): Observable<StyrtabellRedovisningshanvisningDto[]> {
        return this.apiClientService.post<any, StyrtabellRedovisningshanvisningDto[]>("administration/styrtabeller/redovisningshanvisning", {
            mottagningsplats: searchModel.mottagningsplats,
            mottagare: searchModel.mottagare,
            inmatningshandelssortiment: searchModel.inmatningshandelssortiment,
            moPoRoList: selectVerksamhetsomraden
        }, false);
    }

    searchMatinformation(searchModel: IRegelverkViol3SearchModel, selectVerksamhetsomraden: IVerksamhetsomraden[]): Observable<StyrtabellMatningsinformationDto[]> {
        return this.apiClientService.post<any, StyrtabellMatningsinformationDto[]>("administration/styrtabeller/matningsinformation", {
            mottagningsplats: searchModel.mottagningsplats,
            mottagare: searchModel.mottagare,
            inmatningshandelssortiment: searchModel.inmatningshandelssortiment,
            moPoRoList: selectVerksamhetsomraden
        }, false);
    }

    getFilteredRedovisningshanvisningar(
        anskaffningsformer: string[],
        mottagningsplatsId: number,
        befraktare: { huvudkod: string; internummer: string },
        inmatningshandelssortiment: string,
        transportslag?: string,
        kontraktstyp?: string
    ): Observable<RedovisningshanvisningDto[]> {
        const befraktareField = ObjectUtils.isNullOrUndefined(befraktare) ? "" : `${befraktare.huvudkod}-${befraktare.internummer}`;
        const params = {
            anskaffningsformer,
            mottagningsplatsId,
            befraktare: befraktareField,
            inmatningshandelssortiment,
            ...(transportslag && { transportslag }),
            ...(kontraktstyp && { kontraktstyp })
        };
        return this.apiClientService.get("administration/styrtabeller/applicableRedovisningshanvisningar", false, params);
    }

    getSelectedLkd(selectionItems: SelectionItem[]): StyrtabellLanKommunDistriktDTO[] {
        const valda: StyrtabellLanKommunDistriktDTO[] = [];
        selectionItems.forEach(lan => {
            if (lan.selected) {
                const valtLan = new StyrtabellLanKommunDistriktDTO();
                valtLan.lanKod = lan.value;
                valda.push(valtLan);
            } else if (lan.partialSelected) {
                lan.children.forEach(kommun => {
                    if (kommun.selected) {
                        const valdKommum = new StyrtabellLanKommunDistriktDTO();
                        valdKommum.kommunKod = kommun.value;
                        valda.push(valdKommum);
                    } else if (kommun.partialSelected) {
                        kommun.children.forEach(lkd => {
                            if (lkd.selected) {
                                const valdLkd = new StyrtabellLanKommunDistriktDTO();
                                valdLkd.lkdKod = lkd.value;
                                valda.push(valdLkd);
                            }
                        });
                    }

                });
            }
        });
        return valda;
    }
}
